import React from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@packages/ui/shared';

const faqData = [
    {
        question: 'How do I find information for my vehicle?',
        answer: (
            <>
                <div>
                    Technical information is available through Online Reference (PDF-based documents) and Service
                    Diagnostics (interactive HTML). Online Reference can be searched using{' '}
                    <strong>
                        Document Code / Literature # / Bulletin #, Publication Type, Vehicle Identification Number
                        (VIN), Model Year, Carline, Trim, Keyword(s), and Tool #
                    </strong>
                    .
                    <br />
                    Need help finding the right information? View the STIS User Guide for step-by-step guidance on
                    searching, filtering, reviewing results, and opening the appropriate document.
                </div>
                <div>
                    <Link to="/stis">Open TechShare STIS.</Link>
                </div>
            </>
        ),
    },
    {
        question: 'Are there charges or access limitations for STIS information?',
        answer: (
            <>
                Some STIS content requires an active subscription. Subscription options and current pricing are
                maintained on SubaruDiagnostics.com. You can browse the public TechShare site and search available
                information before signing in, but locked content requires an eligible account or subscription.
            </>
        ),
    },
    {
        question: 'Why should I return to STIS after downloading information? ',
        answer: (
            <>
                Subaru service information is updated as new and revised publications are released. Return to TechShare
                STIS before performing a repair to confirm that you are using the latest available Technical Service
                Bulletin, service procedure, or diagnostic information.
            </>
        ),
    },
    {
        question: 'How do I search STIS Publications?',
        answer: (
            <>
                Enter a symptom, DTC, document code, or other specific term in the search bar. Use vehicle and
                Publication Type filters to narrow the results. Review the Keyword Preview before opening a document to
                see relevant snippets and page references. If the first search does not return what you need, try a more
                specific term, an alternate term, or the exact document code including punctuation.
            </>
        ),
    },
    {
        question: 'How can I access State I/M Program Advisory Bulletins for free?',
        answer: (
            <>
                Open STIS Publications and select Filters. Set Publication Type to “State Inspection/Maintenance
                Advisory Bulletin.” Optionally select the applicable vehicle information, then apply the filters and
                open the available bulletin. These bulletins are available without a subscription.
            </>
        ),
    },
    {
        question: 'How much vehicle information do I need to search?',
        answer: (
            <>
                A VIN provides the most focused vehicle-specific experience, but it is not required for every search.
                You can also search using Model Year and Carline, then add Trim, Model Code, Publication Type, or
                Document Code as needed.
            </>
        ),
    },
    {
        question: 'What do the abbreviations in Subaru publications mean?',
        answer: (
            <>
                Abbreviations vary by model, model year, and publication. Select the applicable vehicle, then search for
                “abbreviation” and use Keyword Preview to locate the abbreviation list in the relevant Owner’s Manual or
                Service Manual.
            </>
        ),
    },
    {
        question: 'Can I view documents without a subscription? ',
        answer: (
            <>
                <div>
                    You can browse and search STIS Publications without signing in. Documents that are available without
                    a subscription can be opened directly. A lock indicates that a sign-in and eligible subscription are
                    required. For detailed navigation instructions, use the TechShare user documentation.
                </div>
                <Link to="/stis">Open TechShare STIS.</Link>
            </>
        ),
    },
    {
        question: 'Can I see what a document covers before I subscribe?',
        answer: (
            <>
                Yes. From the public search results, review the document title and available description to determine
                whether the publication is relevant. Hover over the title when a description is available. Full document
                content and Keyword Preview may require the appropriate access.
            </>
        ),
    },
    {
        question: 'Can I order printed manuals or Technical Service Bulletins? ',
        answer: (
            <>
                No. Manuals, Technical Service Bulletins, and other STIS publications are provided digitally through
                TechShare. Access may require an active subscription.{' '}
            </>
        ),
    },
    {
        question: 'What browser should I use with TechShare? ',
        answer: (
            <>
                Microsoft Edge is the preferred browser for TechShare. JavaScript and cookies must be enabled. If Google
                Chrome blocks a Service Diagnostics window or document, follow the on-screen instructions to allow
                pop-ups and redirects for TechShare, then try again.
            </>
        ),
    },
    {
        question: 'I cannot sign in. What should I try? ',
        answer: (
            <>
                <ol type="1">
                    <li>Confirm that your username and password are entered correctly.</li>
                    <li>Make sure Caps Lock is off.</li>
                    <li>Use the Forgot Password option if you need to reset your password.</li>
                    <li>Close extra sign-in tabs, clear your browser cache, and try again in Microsoft Edge.</li>
                    <li>
                        If the problem continues, capture the error message and contact support. Include your username,
                        account or order number if applicable, the date and time of the attempt, and a screenshot of the
                        error.
                    </li>
                </ol>
                <div>
                    For Subaru Owners and Non-Subaru Personnel: Customer Service:{' '}
                    <a href="tel:1-800-782-2873">1-800-782-2873</a>
                </div>
            </>
        ),
    },
    {
        question: 'Where can I purchase Subaru diagnostic tools?',
        answer: (
            <>
                <div>
                    Current information about Subaru-specific diagnostic tools, availability, and ordering is provided
                    on the Diagnostic Tool Information page. Product availability and purchasing details may change, so
                    use that page rather than relying on prices listed in the FAQ.
                </div>
                <div>
                    <Link to="/public/diagnostic-tool-info">View Diagnostic Tool Information.</Link>
                </div>
            </>
        ),
    },
    {
        question: 'How do I obtain a Subaru OEM Diagnostic Interface tool?',
        answer: (
            <>
                <div>
                    Subaru OEM diagnostic interface tools are available for the U.S. market. Authorized Subaru retailers
                    and independent repair facilities should follow the current ordering instructions on the Diagnostic
                    Tool Information page. For questions about orders, installation, or hardware support, use the
                    contact information shown on that page.
                </div>
                <div>
                    <Link to="/public/diagnostic-tool-info">View Diagnostic Tool Information.</Link>
                </div>
            </>
        ),
    },
    {
        question: 'Why can’t I download a PDF even though I have access?',
        answer: (
            <>
                Try the following:
                <ol type="1">
                    <li>Select the Download icon once and check your browser’s Downloads folder.</li>
                    <li>Allow downloads and pop-ups for TechShare if your browser blocks them.</li>
                    <li>Refresh the page and open the document again.</li>
                    <li>Confirm that your subscription or entitlement is still active.</li>
                    <li>
                        If the issue continues, capture the document code, error message, date and time, and a
                        screenshot, then contact support.
                    </li>
                </ol>
            </>
        ),
    },
    {
        question: 'What does the “R” at the end of a Technical Service Bulletin number mean?',
        answer: (
            <>
                An “R” at the end of a Technical Service Bulletin number identifies a revised bulletin. Use the revised
                version and confirm that its applicability matches the vehicle before following the procedure.
            </>
        ),
    },
    {
        question: 'What is a PDF?',
        answer: (
            <>
                <div>
                    PDF (Portable Document Format) is a universal file format that preserves fonts, formatting, colors,
                    and graphics of a source document. PDF files can be viewed, navigated, and printed using a
                    compatible PDF reader.
                </div>
                <div>
                    <a href="https://get.adobe.com/reader/" target="_blank" rel="noopener noreferrer">
                        Download a PDF reader.
                    </a>
                </div>
            </>
        ),
    },
    {
        question: 'What do the numbers in a Technical Service Bulletin number mean?',
        answer: (
            <>
                Service Bulletin numbers have three components (in this order): subject code, a sequential number, and
                year created (displayed as 00-000-00). The following is a list of the subject codes used:
                <ol type="1">
                    <li>General Specifications, Get Ready Procedures and Lubrication</li>
                    <li>Engine: Cylinder Heads, Crankcase, Clutch, Manifolds, System Engine Accessories</li>
                    <li>Drive Train: Transaxle, Axle Shafts, Shifter System</li>
                    <li>Steering</li>
                    <li>
                        Suspension: Road Wheels and Tires, Shocks, Springs, Torsion Bars, Control Arms, Trailing Arms
                    </li>
                    <li>
                        Brakes: Master Cylinder, Wheel Cylinder, Calipers, Drums and Rotors, Lining and Pads, Hand Brake
                    </li>
                    <li>Electrical: Battery Ignition, Charging and Starting, Circuits, Lights, Switches and Motors</li>
                    <li>Instruments and Controls: Motors, Pedal Assemblies, Cables</li>
                    <li>
                        Cooling, Fuel, and Exhaust Systems: Radiator, Water Pump, and Hoses, Thermostat, Fuel Tank, Fuel
                        Pump, Exhaust Pipe, Resonator, Manifold, and Mounting
                    </li>
                    <li>Heating Ventilation: A/C Systems, Heater Core, Hoses</li>
                    <li>Emission Systems: Exhaust and Evaporative Systems</li>
                    <li>Body: Glass, Sheet Metal, Trim, Upholstery</li>
                    <li>Paint</li>
                    <li>Special Tools: Applications, Modifications</li>
                    <li>Accessory Parts:</li>
                    <li>Automatic Transmission</li>
                    <li>Occupant Restraint: Air Bags, Seatbelts</li>
                    <li>Service Manual Corrections: Corrections/additions to vehicle service manuals</li>
                    <li>Hybrid and EV</li>
                </ol>
            </>
        ),
    },
];

type FAQItemProps = {
    question: string;
    answer: React.ReactNode;
    open: boolean;
    onClick: () => void;
};

const FAQItem: React.FC<FAQItemProps> = ({ question, answer, open, onClick }) => (
    <div
        style={{
            width: '100%',
            gap: 8,
            padding: 8,
            borderRadius: 8,
            marginBottom: 16,
            cursor: 'pointer',
            border: '1px solid #E3E3E3',
            display: 'flex',
            flexDirection: 'column',
        }}
    >
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                height: 45,
                padding: 8,
                cursor: 'pointer',
            }}
            onClick={onClick}
        >
            <Icon name={open ? 'chevron-down' : 'chevron-right'} color="blueOne" />
            <span
                style={{
                    fontFamily: 'Roboto, Arial, Helvetica, sans-serif',
                    fontWeight: 600,
                    fontSize: 18,
                    lineHeight: '160%',
                    letterSpacing: 0,
                    color: '#1F518F',
                }}
            >
                {question}
            </span>
        </div>
        {open && (
            <div
                style={{
                    width: 'calc(100% - 40px)',
                    minHeight: 70,
                    paddingLeft: 40,
                    paddingRight: 8,
                    gap: 16,
                    fontFamily: 'Roboto, Arial, Helvetica, sans-serif',
                    fontWeight: 400,
                    fontSize: 15,
                    lineHeight: '160%',
                    letterSpacing: 0,
                    color: '#4C525D',
                    marginTop: 8,
                }}
            >
                {answer}
            </div>
        )}
    </div>
);

export const FAQ: React.FC = () => {
    const [openIndex, setOpenIndex] = React.useState<number | null>(null);

    return (
        <div
            data-testid="faq"
            style={{
                maxWidth: '90%',
                margin: '0 auto',
                padding: '2rem',
                fontFamily: 'Arial, Helvetica, sans-serif',
                fontSize: 15,
                color: '#000',
                background: '#fff',
            }}
        >
            <h1
                style={{
                    color: '#000',
                    fontFamily: 'Roboto, Arial, Helvetica, sans-serif',
                    fontWeight: 400,
                    fontSize: 35,
                    lineHeight: '56px',
                    letterSpacing: '0%',
                }}
            >
                FAQ'S
            </h1>
            <div>
                {faqData.map((faq, idx) => (
                    <FAQItem
                        key={faq.question}
                        question={faq.question}
                        answer={faq.answer}
                        open={openIndex === idx}
                        onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                    />
                ))}
            </div>
        </div>
    );
};
